import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { useDocumentMeta } from '../utils/useDocumentMeta';

/**
 * Shared shell for the three legal pages (/impresszum, /aszf, /adatvedelem).
 * Keeps SEO meta + header/footer + back-link consistent; the page body is
 * supplied by `children` as semantic HTML.
 */
export const LegalPageLayout = ({
  testId,
  title,
  metaDescription,
  canonicalPath,
  updatedAt,
  children,
}) => {
  const canonicalUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${canonicalPath}`
    : `https://oroszcoaching.hu${canonicalPath}`;

  useDocumentMeta({
    title: `${title} | Orosz Coaching`,
    description: metaDescription,
    canonical: canonicalUrl,
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: metaDescription,
      url: canonicalUrl,
      inLanguage: 'hu-HU',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Orosz Coaching',
        url: typeof window !== 'undefined' ? window.location.origin : 'https://oroszcoaching.hu',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Orosz Coaching',
        email: 'info@oroszcoaching.hu',
      },
      dateModified: updatedAt,
    },
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <article data-testid={testId} className="pt-28 pb-20">
        <div className="container mx-auto px-6 max-w-3xl">
          <Link
            to="/"
            data-testid="legal-back-link"
            className="inline-flex items-center text-sm text-yellow-700 hover:text-yellow-600 mb-6 transition-colors"
          >
            <ArrowLeft className="mr-1.5" size={14} />
            Vissza a főoldalra
          </Link>

          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="text-yellow-600" size={22} />
            <span className="text-xs font-bold tracking-widest uppercase text-yellow-600">Jogi</span>
          </div>

          <h1
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            {title}
          </h1>
          <p className="text-sm text-gray-500 mb-10">Utolsó frissítés: {updatedAt}</p>

          <div className="legal-prose space-y-6 text-gray-700 leading-relaxed">
            {children}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default LegalPageLayout;
